import {
  Badge,
  Box,
  Collapse,
  Flex,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Spinner,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { RiCheckLine, RiQuestionAnswerLine } from 'react-icons/ri';
import { MdOutlineKeyboardArrowDown, MdOutlineSmartToy, MdCompareArrows } from 'react-icons/md';
import { FiChevronRight } from 'react-icons/fi';
import {
  PerspectiveBaseInfoFragment,
  useGetPerspectiveByDtTopicProjectLazyQuery,
  useGetAnswerSimilaritiesLazyQuery,
} from '../../generated/graphql';
import { HelivanColor } from '../helivan/HelivanColor';
import { HelivanText } from '../helivan/HelivanText';
import { getSimilarityStyle } from './ProjectTabViewFaq';
import { formatVersionLabel } from '../../utils/formatDate';

interface PersonalInspectProps {
  allDigitalTwins: { id: string; name: string }[];
  digitalTwinId?: string;
  setDigitalTwinId: (id: string | undefined) => void;
  // Per-topic perspectives for the current version (carry topic + blurb).
  perspectives: PerspectiveBaseInfoFragment[];
  projectId: string;
  // Other versions of this watch, for the optional "compare with" view.
  timelineProjects?: { id: string; dateCreated: string }[];
}

const entityIdOf = (p: PerspectiveBaseInfoFragment) => p.customAgent?.id ?? p.digitalTwin?.id;

interface QARow {
  question: string;
  answerA?: string;
  answerB?: string;
}

export const PersonalInspect = ({
  allDigitalTwins,
  digitalTwinId,
  setDigitalTwinId,
  perspectives,
  projectId,
  timelineProjects = [],
}: PersonalInspectProps) => {
  useEffect(() => {
    if (!digitalTwinId && allDigitalTwins.length > 0) {
      setDigitalTwinId(allDigitalTwins[0].id);
    }
  }, [digitalTwinId, allDigitalTwins, setDigitalTwinId]);

  const selectedAgent = allDigitalTwins.find((a) => a.id === digitalTwinId);

  // The agent's per-topic perspectives (exclude the 'topic' overall row).
  const topicPerspectives = useMemo(
    () =>
      perspectives
        .filter((p) => p.topic !== 'topic' && entityIdOf(p) === digitalTwinId)
        .sort((a, b) => a.topic.localeCompare(b.topic)),
    [perspectives, digitalTwinId],
  );

  // Compare against an earlier version (answer-similarity shading).
  const [compareVersionId, setCompareVersionId] = useState<string | null>(null);
  const otherVersions = useMemo(
    () => timelineProjects.filter((tp) => tp.id !== projectId),
    [timelineProjects, projectId],
  );
  const compareVersion = timelineProjects.find((tp) => tp.id === compareVersionId);
  const allVersionDates = useMemo(() => timelineProjects.map((tp) => tp.dateCreated), [timelineProjects]);
  const isComparing = !!compareVersionId;

  // Per-topic lazily-loaded Q&A + similarity.
  const [expanded, setExpanded] = useState<string | null>(null);
  const [qaByTopic, setQaByTopic] = useState<Record<string, QARow[]>>({});
  const [simByTopic, setSimByTopic] = useState<Record<string, Map<string, number | null>>>({});
  const [loadingTopic, setLoadingTopic] = useState<string | null>(null);
  const [fetchA] = useGetPerspectiveByDtTopicProjectLazyQuery({ fetchPolicy: 'cache-first' });
  const [fetchB] = useGetPerspectiveByDtTopicProjectLazyQuery({ fetchPolicy: 'cache-first' });
  const [fetchSims] = useGetAnswerSimilaritiesLazyQuery({ fetchPolicy: 'network-only' });

  // Reset cached comparison data when the compared version changes.
  useEffect(() => {
    setQaByTopic({});
    setSimByTopic({});
  }, [compareVersionId, digitalTwinId]);

  const loadTopic = async (topic: string) => {
    if (!digitalTwinId) return;
    setLoadingTopic(topic);
    try {
      const aRes = await fetchA({ variables: { dtId: digitalTwinId, topic, projectId } });
      const opsA = aRes.data?.getPerspectiveByDtTopicProject?.opinions ?? [];
      const rows: QARow[] = opsA.map((o) => ({ question: o.question, answerA: o.answer }));

      if (compareVersionId) {
        const [bRes, simRes] = await Promise.all([
          fetchB({ variables: { dtId: digitalTwinId, topic, projectId: compareVersionId } }),
          fetchSims({ variables: { agentId: digitalTwinId, topic, versionAId: projectId, versionBId: compareVersionId } }),
        ]);
        const answersB = new Map(
          (bRes.data?.getPerspectiveByDtTopicProject?.opinions ?? []).map((o) => [o.question, o.answer]),
        );
        const seen = new Set(rows.map((r) => r.question));
        rows.forEach((r) => { r.answerB = answersB.get(r.question); });
        answersB.forEach((ans, q) => { if (!seen.has(q)) rows.push({ question: q, answerB: ans }); });

        const simMap = new Map<string, number | null>();
        (simRes.data?.getAnswerSimilarities ?? []).forEach((s) => simMap.set(s.question, s.similarity ?? null));
        setSimByTopic((prev) => ({ ...prev, [topic]: simMap }));
      }

      setQaByTopic((prev) => ({ ...prev, [topic]: rows }));
    } catch {
      setQaByTopic((prev) => ({ ...prev, [topic]: [] }));
    } finally {
      setLoadingTopic(null);
    }
  };

  const toggleTopic = (topic: string) => {
    if (expanded === topic) {
      setExpanded(null);
      return;
    }
    setExpanded(topic);
    if (qaByTopic[topic] === undefined) loadTopic(topic);
  };

  return (
    <Flex width="full" height="full" flexDirection="column" maxWidth="900px" mx="auto">
      {/* Header */}
      <Flex
        width="full"
        flexDirection={{ base: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ base: 'stretch', md: 'center' }}
        gap={{ base: 3, md: 0 }}
        mb={4}
        pb={4}
        borderBottom="1px solid"
        borderBottomColor={HelivanColor.border.primary}
      >
        <HStack spacing={3}>
          <Flex width="36px" height="36px" borderRadius="8px" bg={HelivanColor.hover.lightBlue} alignItems="center" justifyContent="center">
            <RiQuestionAnswerLine size={18} color={HelivanColor.text.helivanBlue} />
          </Flex>
          <VStack align="start" spacing={0}>
            <HelivanText variant="smallBody+" colorOverride={HelivanColor.text.blueDark}>Responses</HelivanText>
            <HelivanText variant="tinyBody" colorOverride={HelivanColor.text.blueMid}>
              What the agent said on each topic this snapshot
            </HelivanText>
          </VStack>
        </HStack>

        <HStack spacing={3} flexWrap="wrap" justifyContent={{ base: 'flex-start', md: 'flex-end' }}>
          {allDigitalTwins.length > 1 && (
            <Menu isLazy>
              <MenuButton
                as={Box}
                px={3}
                py={2}
                borderRadius="6px"
                bg="white"
                border="1px solid"
                borderColor={HelivanColor.border.primary}
                cursor="pointer"
                minWidth="180px"
                _hover={{ borderColor: HelivanColor.text.helivanBlue }}
              >
                <HStack justify="space-between">
                  <HStack spacing={2}>
                    <MdOutlineSmartToy size={14} color={HelivanColor.text.blueMid} />
                    <Text fontSize="13px" color={selectedAgent ? HelivanColor.text.blueDark : HelivanColor.text.blueMid} noOfLines={1} maxWidth="120px">
                      {selectedAgent ? selectedAgent.name : 'Select agent'}
                    </Text>
                  </HStack>
                  <MdOutlineKeyboardArrowDown size={16} color={HelivanColor.text.blueMid} />
                </HStack>
              </MenuButton>
              <Portal>
                <MenuList zIndex={1400} maxHeight="300px" overflowY="auto" py={2}>
                  {allDigitalTwins.map((agent) => (
                    <MenuItem key={agent.id} onClick={() => setDigitalTwinId(agent.id)} fontSize="13px" py={2} px={3}>
                      <HStack spacing={2} width="full" justify="space-between">
                        <HStack spacing={2}>
                          <MdOutlineSmartToy size={14} color={HelivanColor.text.blueMid} />
                          <Text color={HelivanColor.text.blueDark}>{agent.name}</Text>
                        </HStack>
                        {digitalTwinId === agent.id && <RiCheckLine size={14} color={HelivanColor.text.helivanBlue} />}
                      </HStack>
                    </MenuItem>
                  ))}
                </MenuList>
              </Portal>
            </Menu>
          )}

          {otherVersions.length > 0 && (
            <Menu isLazy>
              <MenuButton
                as={Box}
                px={3}
                py={2}
                borderRadius="6px"
                bg={isComparing ? HelivanColor.hover.lightBlue : 'white'}
                border="1px solid"
                borderColor={HelivanColor.border.primary}
                cursor="pointer"
                minWidth="150px"
                _hover={{ borderColor: HelivanColor.text.helivanBlue }}
              >
                <HStack justify="space-between">
                  <HStack spacing={2}>
                    <MdCompareArrows size={14} color={HelivanColor.text.blueMid} />
                    <Text fontSize="13px" color={HelivanColor.text.blueDark} noOfLines={1} maxWidth="110px">
                      {compareVersion ? `vs ${formatVersionLabel(allVersionDates, compareVersion.dateCreated)}` : 'Compare with…'}
                    </Text>
                  </HStack>
                  <MdOutlineKeyboardArrowDown size={16} color={HelivanColor.text.blueMid} />
                </HStack>
              </MenuButton>
              <Portal>
                <MenuList zIndex={1400} maxHeight="300px" overflowY="auto" py={2}>
                  <MenuItem onClick={() => setCompareVersionId(null)} fontSize="13px" py={2} px={3}>
                    <HStack spacing={2} width="full" justify="space-between">
                      <Text color={HelivanColor.text.blueDark}>None (single version)</Text>
                      {!compareVersionId && <RiCheckLine size={14} color={HelivanColor.text.helivanBlue} />}
                    </HStack>
                  </MenuItem>
                  {otherVersions.map((tp) => (
                    <MenuItem key={tp.id} onClick={() => setCompareVersionId(tp.id)} fontSize="13px" py={2} px={3}>
                      <HStack spacing={2} width="full" justify="space-between">
                        <Text color={HelivanColor.text.blueDark}>{formatVersionLabel(allVersionDates, tp.dateCreated)}</Text>
                        {compareVersionId === tp.id && <RiCheckLine size={14} color={HelivanColor.text.helivanBlue} />}
                      </HStack>
                    </MenuItem>
                  ))}
                </MenuList>
              </Portal>
            </Menu>
          )}
        </HStack>
      </Flex>

      {/* Topic list */}
      <Flex flex={1} overflowY="auto" flexDirection="column">
        {topicPerspectives.length === 0 ? (
          <Flex width="full" py={16} justifyContent="center" alignItems="center" bg="white" border="1px solid" borderColor={HelivanColor.border.primary} borderRadius="12px">
            <VStack spacing={2}>
              <RiQuestionAnswerLine size={28} color={HelivanColor.text.blueMid} />
              <Text fontSize="14px" fontWeight="600" color={HelivanColor.text.blueDark}>No responses in this snapshot</Text>
            </VStack>
          </Flex>
        ) : (
          <VStack spacing={3} width="full" align="stretch">
            {topicPerspectives.map((p) => {
              const isOpen = expanded === p.topic;
              const rows = qaByTopic[p.topic];
              const sims = simByTopic[p.topic];
              return (
                <Box
                  key={p.id}
                  bg="white"
                  border="1px solid"
                  borderColor={HelivanColor.border.primary}
                  borderRadius="12px"
                  overflow="hidden"
                >
                  {/* Topic header (clickable) */}
                  <Flex
                    p={4}
                    align="center"
                    justify="space-between"
                    cursor="pointer"
                    onClick={() => toggleTopic(p.topic)}
                    _hover={{ bg: HelivanColor.background.secondary }}
                    gap={3}
                  >
                    <HStack spacing={3} align="center" flex={1} minWidth={0}>
                      <Box transform={isOpen ? 'rotate(90deg)' : 'none'} transition="transform 0.15s" color={HelivanColor.text.blueMid} flexShrink={0}>
                        <FiChevronRight size={16} />
                      </Box>
                      <VStack align="start" spacing={0} flex={1} minWidth={0}>
                        <Text fontSize="14px" fontWeight={600} color={HelivanColor.text.blueDark} noOfLines={1}>{p.topic}</Text>
                        {p.blurb && !p.blurb.startsWith('Summary blurb not generated') && (
                          <Text fontSize="12px" color={HelivanColor.text.blueMid} noOfLines={1}>{p.blurb}</Text>
                        )}
                      </VStack>
                    </HStack>
                  </Flex>

                  {/* Q&A body */}
                  <Collapse in={isOpen} animateOpacity>
                    <Box borderTop="1px solid" borderTopColor={HelivanColor.border.primary} p={4} bg={HelivanColor.background.primary}>
                      {loadingTopic === p.topic || rows === undefined ? (
                        <HStack spacing={2} py={4} justify="center">
                          <Spinner size="sm" color={HelivanColor.text.helivanBlue} />
                          <Text fontSize="13px" color={HelivanColor.text.blueMid}>Loading responses…</Text>
                        </HStack>
                      ) : rows.length === 0 ? (
                        <Text fontSize="13px" color={HelivanColor.text.blueMid}>No responses recorded for this topic.</Text>
                      ) : (
                        <VStack spacing={3} align="stretch">
                          {rows.map((r, idx) => {
                            const sim = isComparing ? getSimilarityStyle(sims?.get(r.question)) : null;
                            return (
                              <Box key={idx} bg="white" border="1px solid" borderColor={HelivanColor.border.primary} borderRadius="10px" overflow="hidden">
                                <Flex p={3} bg={HelivanColor.background.secondary} borderBottom="1px solid" borderBottomColor={HelivanColor.border.primary} align="center" justify="space-between" gap={2}>
                                  <Text fontSize="13px" fontWeight={500} color={HelivanColor.text.blueDark}>{r.question}</Text>
                                  {sim && (
                                    <Tooltip label={`How much this answer moved vs ${compareVersion ? formatVersionLabel(allVersionDates, compareVersion.dateCreated) : "earlier version"}`} hasArrow placement="top">
                                      <Badge colorScheme={sim.scheme} fontSize="11px" flexShrink={0}>{sim.label}</Badge>
                                    </Tooltip>
                                  )}
                                </Flex>
                                {isComparing ? (
                                  <Flex flexDirection={{ base: 'column', md: 'row' }}>
                                    <Box
                                      flex={1}
                                      p={3}
                                      borderRight={{ base: 'none', md: '1px solid' }}
                                      borderBottom={{ base: '1px solid', md: 'none' }}
                                      borderColor={HelivanColor.border.primary}
                                    >
                                      <Text fontSize="10px" fontWeight={600} color={HelivanColor.text.blueMid} mb={1}>CURRENT</Text>
                                      <Text fontSize="13px" color={r.answerA ? HelivanColor.text.blueDark : HelivanColor.text.gray} lineHeight="1.6">{r.answerA ?? 'No response'}</Text>
                                    </Box>
                                    <Box flex={1} p={3}>
                                      <Text fontSize="10px" fontWeight={600} color={HelivanColor.text.blueMid} mb={1}>{compareVersion ? formatVersionLabel(allVersionDates, compareVersion.dateCreated).toUpperCase() : "EARLIER"}</Text>
                                      <Text fontSize="13px" color={r.answerB ? HelivanColor.text.blueDark : HelivanColor.text.gray} lineHeight="1.6">{r.answerB ?? 'No response'}</Text>
                                    </Box>
                                  </Flex>
                                ) : (
                                  <Box p={3}>
                                    <Text fontSize="13px" color={HelivanColor.text.blueDark} lineHeight="1.6">{r.answerA ?? 'No response'}</Text>
                                  </Box>
                                )}
                              </Box>
                            );
                          })}
                        </VStack>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </VStack>
        )}
      </Flex>
    </Flex>
  );
};
